/**
 * What the signed-in person can, as the base says it (PLAN-MULTI-TENANT § 5.2, lots L6c and L6g): `my_access()`, the
 * one access function the rules of the tables read, is also the one source of the screens. The front never derives a
 * right of its own from `user_profiles.role` (a copy, PLAN-DETTE 1.31) nor from the team row: it shows what the base
 * opens (principle 11).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '../supabase';

// my_access is not in the generated types (src/types/supabase.ts predates L6c): its answer is validated below.
const db = supabase as unknown as SupabaseClient;

/** The composite `access_context` as PostgREST returns it: every field may be null for a caller outside the team. */
const accessContextSchema = z.object({
  is_member: z.boolean().nullable(),
  is_admin: z.boolean().nullable(),
  roles: z.array(z.string()).nullable(),
  domains: z.array(z.string()).nullable(),
  company_domains: z.array(z.string()).nullable(),
});

export interface MyAccess {
  /** In the team, with an approved profile. Otherwise every other field is empty. */
  readonly isMember: boolean;
  /** Owner or administrator of the company, approved (`has_admin_right`). */
  readonly isAdmin: boolean;
  /** Their roles: the level on the root, and the business roles given on nodes. */
  readonly roles: readonly string[];
  /** The data domains open on at least one node: what the screens may show. */
  readonly domains: readonly string[];
}

/** Nothing: outside the team, unapproved, or while nothing has been read. */
export const NO_ACCESS: MyAccess = Object.freeze({ isMember: false, isAdmin: false, roles: [], domains: [] });

/** The value of `my_access()`, validated; anything else reads as no access (fail closed). Pure. */
export function parseMyAccess(raw: unknown): MyAccess {
  const parsed = accessContextSchema.safeParse(Array.isArray(raw) ? raw[0] : raw);
  if (!parsed.success || parsed.data.is_member !== true) return NO_ACCESS;
  return {
    isMember: true,
    isAdmin: parsed.data.is_admin === true,
    roles: parsed.data.roles ?? [],
    domains: parsed.data.domains ?? [],
  };
}

/** Reads `my_access()` for the signed-in person. Throws on a transport or database error. */
export async function fetchMyAccess(): Promise<MyAccess> {
  const { data, error } = await db.rpc('my_access');
  if (error) throw new Error(error.message);
  return parseMyAccess(data);
}
